import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/core/api'
import { logger } from '@/utils/logger'

export type PullRequest = {
  id: string
  number: number
  title: string
  state: string
  author: string
  author_avatar?: string
  provider: string
  repository_full_name: string
  owner: string
  repo: string
  created_at: string
  updated_at: string
  merged_at?: string
  closed_at?: string
  draft?: boolean
  labels?: string[]
  review_status?: string
  mergeable?: boolean
  ci_status?: string
  additions?: number
  deletions?: number
  comments_count?: number
  reviewers?: unknown[]
  [key: string]: unknown
}

export type PRFilters = {
  search: string
  developers: string[]
  repositories: string[]
  labels: string[]
  reviewStatus: string[]
}

export const usePullRequestsStore = defineStore('pullRequests', () => {
  const pullRequests = ref<PullRequest[]>([])
  const loading = ref(false)
  const syncing = ref(false)
  const lastSyncAt = ref<string | null>(null)

  const filters = ref<PRFilters>({
    search: '',
    developers: [],
    repositories: [],
    labels: [],
    reviewStatus: [],
  })

  const filteredPullRequests = computed(() => {
    let prs = pullRequests.value

    if (filters.value.search) {
      const q = filters.value.search.toLowerCase()
      prs = prs.filter(
        pr =>
          pr.title.toLowerCase().includes(q) ||
          pr.author.toLowerCase().includes(q) ||
          String(pr.number).includes(q),
      )
    }

    if (filters.value.developers.length > 0) {
      prs = prs.filter(pr => filters.value.developers.includes(pr.author))
    }

    if (filters.value.repositories.length > 0) {
      prs = prs.filter(pr => filters.value.repositories.includes(pr.repository_full_name))
    }

    if (filters.value.labels.length > 0) {
      prs = prs.filter(pr =>
        pr.labels?.some(l => filters.value.labels.includes(l)),
      )
    }

    if (filters.value.reviewStatus.length > 0) {
      prs = prs.filter(pr =>
        pr.review_status && filters.value.reviewStatus.includes(pr.review_status),
      )
    }

    return prs
  })

  async function fetchPullRequests(projectId: string) {
    loading.value = true
    try {
      const data = (await api.getProjectPullRequests(projectId)) as PullRequest[]
      pullRequests.value = data
      lastSyncAt.value = new Date().toISOString()
      return data
    } catch (error) {
      logger.error('PRStore', 'Failed to fetch pull requests:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  async function syncPullRequests(projectId: string) {
    syncing.value = true
    try {
      const data = (await api.getProjectPullRequests(projectId)) as PullRequest[]
      pullRequests.value = data
      lastSyncAt.value = new Date().toISOString()
      return data
    } catch (error) {
      logger.error('PRStore', 'Sync failed:', error)
      throw error
    } finally {
      syncing.value = false
    }
  }

  function setFilter<K extends keyof PRFilters>(key: K, value: PRFilters[K]) {
    filters.value[key] = value
  }

  function resetFilters() {
    filters.value = {
      search: '',
      developers: [],
      repositories: [],
      labels: [],
      reviewStatus: [],
    }
  }

  function $reset() {
    pullRequests.value = []
    loading.value = false
    syncing.value = false
    lastSyncAt.value = null
    resetFilters()
  }

  return {
    pullRequests,
    filteredPullRequests,
    loading,
    syncing,
    lastSyncAt,
    filters,
    fetchPullRequests,
    syncPullRequests,
    setFilter,
    resetFilters,
    $reset,
  }
})
